// ============================================================================
// Stage & Environment Configuration
// ============================================================================

export type TStage = "Test" | "Alpha" | "Beta" | "Prod";

type THostnameList = Record<TStage, string>;

const HostnameList: THostnameList = {
  Test: "localhost",
  Alpha: "alpha-us-west-2.radarplus.lab126.amazon.dev",
  Beta: "beta-us-west-2.radarplus.lab126.amazon.dev",
  Prod: "radarplus.lab126.amazon.dev",
};

// Derive the local Test origin from the running dev server so it tracks whatever
// port webpack was started on (e.g. `PORT=1182 npm run server`). Falls back to
// the default dev-server port when window.location is unavailable.
const localOrigin = window?.location?.origin ? window.location.origin : "http://localhost:1180";

const StageOriginMap: THostnameList = {
  Test: localOrigin,
  Alpha: "https://alpha-us-west-2.radarplus.lab126.amazon.dev",
  Beta: "https://beta-us-west-2.radarplus.lab126.amazon.dev",
  Prod: "https://radarplus.lab126.amazon.dev",
};

const currentHostname = window && window.location ? window.location.hostname : "";
export const currentStage = (Object.entries(HostnameList) as [TStage, string][]).find(([, url]) => url === currentHostname)?.[0] ?? "Alpha";

// export const AUTH_ENABLED = currentStage === "Test" ? false : true;
export const AUTH_ENABLED = true;

// ============================================================================
// Backend API Configuration
// ============================================================================

const endpointList: THostnameList = {
  Test: "https://alpha-us-west-2-bk.radarplus.lab126.amazon.dev",
  Alpha: "https://alpha-us-west-2-bk.radarplus.lab126.amazon.dev",
  Beta: "https://beta-us-west-2-bk.radarplus.lab126.amazon.dev",
  Prod: "https://prod-us-west-2-bk.radarplus.lab126.amazon.dev",
};
const endpoint = currentStage ? endpointList[currentStage] : endpointList.Alpha;

/**
 * Origin of this stage's backend (the `-bk` host).
 *
 * Exported so logout can navigate to the backend's `/auth/logout`. That hop is required rather
 * than cosmetic: the Lambda@Edge gate's session cookie is `HttpOnly` on the parent domain, so
 * this app cannot clear it and only the backend can end that half of the session.
 */
export const backendOrigin = endpoint;
const API = (hostname: string) => ({
  // General
  fetch_TestWithReadout: `${hostname}/get_tests_with_readout`,
  fetch_UserList: `${hostname}/getUserList`,
  //Router Config
  fetch_RouterConfig: `${hostname}/get_page_access`,
  // Page Visit Telemetry
  record_PageVisit: `${hostname}/record_page_visit`,
  // Identifier List
  fetch_Project_List: `${hostname}/get_family_and_project`,
  fetch_Phase_List: `${hostname}/get_phase`,
  fetch_Build_List: `${hostname}/get_build`,
  fetch_Test_List: `${hostname}/get_test`,
  fetch_Config_List: `${hostname}/get_config`,
  fetch_PhaseBuild_List: `${hostname}/get_phase_and_build`,
  fetch_FamilySubcategory_List: `${hostname}/get_family_subcategory`,
  fetch_Station_List: `${hostname}/get_fatp_checks`,
  // Shareable Link
  create_ShareableLink: `${hostname}/create_shareable_link`,
  get_ShareableLink: `${hostname}/get_shareable_link`,
  // Operator Management (admin)
  fetch_OperatorList: `${hostname}/operators`,
  create_Operator: `${hostname}/operators/create`,
  delete_Operator: `${hostname}/operators/delete`,
  activate_Operator: `${hostname}/operators/activate`,
  deactivate_Operator: `${hostname}/operators/deactivate`,
  resetPassword_Operator: `${hostname}/operators/reset-password`,
  // File Upload
  upload_File: `${hostname}/file_upload`,
  // Test Spec
  fetch_TestSpec_SpecList: `${hostname}/get_test_spec_list`,
  fetch_TestSpec_SpecDetail: `${hostname}/get_test_spec`,
  fetch_TestSpec_Sequences: `${hostname}/get_test_spec_sequences`,
  fetch_TestSpec_ReadoutMappings: `${hostname}/get_test_spec_readout_mappings`,
  create_TestSpec_SpecDetail: `${hostname}/add_test_spec`,
  update_TestSpec_SpecDetail: `${hostname}/edit_test_spec`,
  delete_TestSpec_SpecDetail: `${hostname}/delete_test_spec`,
  reset_TestSpec_SpecDetail: `${hostname}/reset_test_spec`,
  download_TestSpec_List: `${hostname}/download_test_spec_list`,
  // Family Spec
  fetch_FamilySpec_List: `${hostname}/get_family_spec_list`,
  fetch_FamilySpec_Detail: `${hostname}/get_family_spec`,
  fetch_FamilySpec_VersionHistory: `${hostname}/get_family_spec_version_history`,
  fetch_FamilySpec_Approvers: `${hostname}/get_approvers`,
  fetch_FamilySpec_Committees: `${hostname}/get_committees`,
  add_FamilySpec: `${hostname}/add_family_spec`,
  edit_FamilySpec: `${hostname}/edit_family_spec`,
  discard_FamilySpec: `${hostname}/discard_family_spec`,
  download_FamilySpec_List: `${hostname}/download_family_spec_list`,
  // TestWaterfall
  fetch_TestWaterfall_List: `${hostname}/get_test_waterfall`,
  fetch_TestWaterfall_TimelineOverview: `${hostname}/get_waterfall_timeline_overview`,
  update_TestWaterfall_DSNTest: `${hostname}/edit_test_waterfall`,
  update_TestWaterfall_Config: `${hostname}/edit_config`,
  copy_TestWaterfall_List: `${hostname}/copy_test_waterfall`,
  validate_TestWaterfall_Timeline: `${hostname}/validate_test_timeline`,
  save_TestWaterfall_ExecutionTimeline: `${hostname}/save_execution_timeline`,
  update_TestWaterfall_Milestones: `${hostname}/edit_milestones`,
  fetch_TestWaterfall_Holidays: `${hostname}/get_holidays`,
  update_TestWaterfall_Holidays: `${hostname}/edit_holidays`,
  update_TestWaterfall_Timeline: `${hostname}/edit_test_timeline`,
  // Homepage
  fetch_Home_TopCardsDisplay: `${hostname}/get_top_cards_display`,
  fetch_Home_BuildDisplay: `${hostname}/get_build_cards_display`,
  set_Home_FavoriteBuilds: `${hostname}/set_favorite_build`,
  fetch_Home_BuildOwners: `${hostname}/get_build_owners`,
  get_Home_EmailFrequency: `${hostname}/get_email_frequency`,
  set_Home_EmailFrequency: `${hostname}/set_email_frequency`,
  get_Home_EmailSubscriptionStatus: `${hostname}/get_email_subscription_status`,
  subscribe_Home_BuildEmail: `${hostname}/subscribe_build_email`,
  unsubscribe_Home_BuildEmail: `${hostname}/unsubscribe_build_email`,
  send_Home_EmailToMe: `${hostname}/send_email_to_me`,
  send_Home_EmailToTeam: `${hostname}/send_email_to_team`,
  // Task Center / Data Input
  fetch_TaskCenter_Projects: `${hostname}/get_tasks_progress`,
  // Real DSN
  fetch_RealDSN_List: `${hostname}/get_real_dsn_list`,
  update_RealDSN_Item: `${hostname}/edit_real_dsn`,
  delete_RealDSN_Item: `${hostname}/delete_real_dsn`,
  download_RealDSN_Template: `${hostname}/download_real_dsn_template`,
  upload_RealDSN_BulkData: `${hostname}/upload_real_dsn_bulk`,
  // Import Waterfall
  download_TestWaterfall_Template: `${hostname}/download_waterfall_template`,
  import_TestWaterfall: `${hostname}/import_waterfall`,
  // Build-level export of every recorded readout check (one sheet, one row per check).
  download_ReadoutData: `${hostname}/download_readout_data`,
  // One CSV of every candidate FATP log for a readout check — the side-by-side comparison the
  // log dropdown cannot show. Raw blob body, no BaseResponse envelope.
  download_FATPCandidateLogs: `${hostname}/download_fatp_candidate_logs`,
  // Test Status
  fetch_TestStatus_List: `${hostname}/get_test_status`,
  // Data Overview
  fetch_DataOverview_List: `${hostname}/get_data_overview`,
  // Data Overview — Phase 2 GETs
  fetch_DataOverview_ReadoutList: `${hostname}/get_readout_list`,
  fetch_DataOverview_TestVariableSelections: `${hostname}/get_readout_input_variable_selections`,
  // Update Schedule drawer: major + final readouts with their scheduled dates.
  // Replaces the old get_device_checkpoint_dates (which carried only dates, per device).
  fetch_DataOverview_MajorReadoutList: `${hostname}/get_major_readout_list`,
  // FATP Log Link drawer: "station" is the FATP check (STATION === checkCode).
  // Check list comes from /get_fatp_checks; genealogy records come from
  // /get_batch_fatp_genealogy; the mapping is persisted via /save_batch_fatp_mapping.
  fetch_DataOverview_FATPStations: `${hostname}/get_fatp_checks`,
  // Readouts must be filtered by check — get_readout_list ignores checkCode and
  // would surface readouts not mapped to the check (FK violation on save).
  fetch_DataOverview_FATPReadouts: `${hostname}/get_fatp_readouts`,
  search_DataOverview_FATPLogs: `${hostname}/get_batch_fatp_genealogy`,
  // Data Overview — Phase 2 batch POSTs
  batch_DataOverview_MarkOngoing: `${hostname}/batch_mark_ongoing`,
  batch_DataOverview_MarkCompleted: `${hostname}/batch_mark_completed`,
  batch_DataOverview_Reset: `${hostname}/batch_reset`,
  batch_DataOverview_Approve: `${hostname}/batch_approve`,
  batch_DataOverview_PassReadouts: `${hostname}/batch_pass_readouts`,
  batch_DataOverview_SetVariables: `${hostname}/batch_set_variables`,
  change_DataOverview_ReadoutSchedule: `${hostname}/change_readout_schedule`,
  batch_DataOverview_LinkFATPLogs: `${hostname}/save_batch_fatp_mapping`,
  update_DataOverview_TestStatus: `${hostname}/update_test_status`,
  // Readout Input / Data Input
  fetch_ReadoutInput_TestData: `${hostname}/get_readout_data`,
  fetch_ReadoutInput_Metadata: `${hostname}/get_readout_input_metadata`,
  fetch_ReadoutInput_DeviceList: `${hostname}/get_readout_input_dsn_list`,
  fetch_ReadoutInput_FATPLogOptions: `${hostname}/get_fatp_genealogy`,
  save_ReadoutInput: `${hostname}/save_readout_data`,
  // FDDD (Failure Data Deep Dive)
  fetch_FDDD_CascadingFilterOptions: `${hostname}/get_cascading_filter_options`,
  fetch_FDDD_FailureRateSummary: `${hostname}/get_failure_rate_summary`,
  fetch_PresetList: `${hostname}/get_presets`,
  update_Presets: `${hostname}/update_presets`,
  // Test Summary / Reports
  fetch_TestSummary_Data: `${hostname}/get_test_summary`,
  fetch_IssueSummary: `${hostname}/get_issue_summary`,
  fetch_IssueDetail: `${hostname}/get_issue_detail`,
  fetch_GroupingSequences: `${hostname}/get_grouping_sequences`,
  fetch_ReadoutDataByTest: `${hostname}/get_readout_data_by_test`,
  fetch_AttachmentsByTest: `${hostname}/get_attachments_by_test`,
  update_TestSummary_FAStatus: `${hostname}/update_fa_status`,
  export_TestSummary_Excel: `${hostname}/export_test_summary_excel`,
  download_TestReport: `${hostname}/download_test_report`,
  download_TestSummary_MeasurementData: `${hostname}/download_measurement_data`,
  // Test Settings — Sequence (shares /get_grouping_sequences with fetch_GroupingSequences above)
  fetch_TestSettings_SequenceList: `${hostname}/get_grouping_sequences`,
  add_TestSettings_Sequence: `${hostname}/add_sequence`,
  edit_TestSettings_Sequence: `${hostname}/edit_sequence`,
  delete_TestSettings_Sequence: `${hostname}/delete_sequence`,
  copy_TestSettings_Sequence: `${hostname}/copy_sequence`,
  fetch_TestSettings_CopySequenceOptions: `${hostname}/get_copy_sequence_options`,
  fetch_TestSettings_DeleteSequenceOptions: `${hostname}/get_delete_sequence_options`,
  // Test Settings — Readout
  fetch_TestSettings_ReadoutList: `${hostname}/get_readout_checks`,
  add_TestSettings_Readout: `${hostname}/add_readout`,
  edit_TestSettings_Readout: `${hostname}/edit_readout`,
  delete_TestSettings_Readout: `${hostname}/delete_readout`,
  fetch_TestSettings_DeleteReadoutOptions: `${hostname}/get_delete_readout_options`,
  download_TestSettings_ReadoutTemplate: `${hostname}/download_readout_template`,
  upload_TestSettings_Readouts: `${hostname}/import_readout`,
  fetch_TestSettings_ReadoutTypeMaster: `${hostname}/get_master_readout_keys`,
  // Test Settings — Check Mapping (shares /get_readout_checks with fetch_TestSettings_ReadoutList above)
  fetch_TestSettings_CheckMapping: `${hostname}/get_readout_checks`,
  update_TestSettings_CheckMapping: `${hostname}/edit_readout_checks`,
  reset_TestSettings_CheckMapping: `${hostname}/reset_readout_checks`,
  // Test Settings — Measurement Mapping
  fetch_TestSettings_MeasurementMapping: `${hostname}/get_readout_measurements`,
  update_TestSettings_MeasurementMapping: `${hostname}/edit_readout_measurements`,
  // Test Settings — Test Variable
  fetch_TestSettings_TestVariables: `${hostname}/get_test_variables`,
  update_TestSettings_TestVariableApplyStatus: `${hostname}/update_test_variable_apply_status`,
  // Test Settings — Equipment
  fetch_TestSettings_EquipmentList: `${hostname}/get_test_settings_equipment_list`,
  fetch_TestSettings_EquipmentListForSequence: `${hostname}/get_equipment_list_for_sequence`,
  // Test Assignment
  fetch_TestAssignment_List: `${hostname}/get_tests_list`,
  update_TestAssignment: `${hostname}/assign_op`,
  fetch_OP_List: `${hostname}/get_op_list`,
  // FA Tracker
  fetch_FATracker_ProjectList: `${hostname}/get_fa_tracker_project_list`,
  fetch_FATracker_Summary: `${hostname}/get_fa_summary`,
  fetch_FATracker_FALog: `${hostname}/get_fa_log`,
  fetch_FATracker_NewFindings: `${hostname}/get_new_findings`,
  fetch_FATracker_NewFindingsFilterOptions: `${hostname}/get_new_findings_filter_options`,
  fetch_FATracker_FADetail: `${hostname}/get_fa_record_detail`,
  update_FATracker_FADetail: `${hostname}/update_fa_record`,
  update_FATracker_UpdateAll: `${hostname}/update_all_fa_records`,
  create_FATracker_FA: `${hostname}/create_fa_record`,
  create_FATracker_MarkWaived: `${hostname}/mark_findings_waived`,
  fetch_FATracker_SmartSummary: `${hostname}/get_jira_smart_summary`,
  batch_FATracker_SmartSummary: `${hostname}/batch_generate_smart_summary`,
  unlink_FATracker_Finding: `${hostname}/unlink_finding`,
  // UV Visualizer
  fetch_UVVisualizer_ColorTable: `${hostname}/get_color_table`,
  // FATP Viewer
  // Distinct from DataOverview's fetch_DataOverview_FATPReadouts (/get_fatp_readouts):
  // this endpoint returns the readouts already nested as a tests → readouts tree.
  fetch_FATPViewer_ReadoutTree: `${hostname}/get_readouts_with_fatp_check`,
  fetch_FATPViewer_DeviceBuckets: `${hostname}/get_fatp_configs`,
  fetch_FATPViewer_Parameters: `${hostname}/get_fatp_parameters`,
  fetch_FATPViewer_StationData: `${hostname}/get_fatp_parameter_data`,
  // Raw parameter export — every parameter of the selected logs, no parameter selection.
  // Asynchronous on purpose: a worst-case selection is ~275k rows / ~160 MB and takes ~70s to
  // build, past the 60s ALB idle timeout, so the job is submitted, polled, then downloaded from
  // a presigned S3 URL rather than returned inline.
  start_FATPViewer_RawExport: `${hostname}/start_fatp_raw_export`,
  fetch_FATPViewer_RawExportStatus: `${hostname}/get_fatp_raw_export_status`,
  // Issue List
  fetch_IssueList: `${hostname}/get_issue_list`,
  add_Issue: `${hostname}/add_issue_record`,
  edit_Issue: `${hostname}/edit_issue_record`,
  delete_Issue: `${hostname}/delete_issue_record`,
  copy_Issue: `${hostname}/copy_issue_record`,
  merge_Issue: `${hostname}/merge_issue_record`,
  fetch_IssueListForCopy: `${hostname}/get_issue_list`,
  // ESD Calculator
  predict_ESD_Reliability: `${hostname}/esd/predict_reliability`,
  generate_ESD_TestPlan: `${hostname}/esd/generate_test_plan`,
  fetch_ESD_ProjectData: `${hostname}/esd/get_project_data`,
  import_ESD_TestPlan: `${hostname}/esd/import_test_plan`,
  save_ESD_Settings: `${hostname}/esd/save_settings`,
  fetch_ESD_Settings: `${hostname}/esd/get_settings`,
  // Ball Impact Calculator
  fetch_BallImpact_ProjectData: `${hostname}/ball_impact/get_project_data`,
  // Stress-Strength Calculator
  fetch_StressStrength_ImportData: `${hostname}/import_data_for_strength_distribution_analysis`,
  // Approval Center
  fetch_Approval_List: `${hostname}/get_approval_list`,
  fetch_Approval_Detail: `${hostname}/get_approval_detail`,
  post_Approval_Submit: `${hostname}/submit_for_approval`,
  post_Approval_SubmitBuildSignOff: `${hostname}/submit_build_sign_off`,
  post_Approval_Approve: `${hostname}/approve_request`,
  post_Approval_Reject: `${hostname}/reject_request`,
  post_Approval_Recall: `${hostname}/recall_request`,
  post_Approval_AddApprover: `${hostname}/add_approver`,
  post_Approval_Transfer: `${hostname}/transfer_approval`,
  post_Approval_BatchApprove: `${hostname}/batch_approve`,
  submit_TestSpec_SignOff: `${hostname}/submit_test_spec_for_sign_off`,
  // Equipment Booking
  fetch_EquipmentBooking_Equipment: `${hostname}/get_bookable_equipment`,
  fetch_EquipmentBooking_Bookings: `${hostname}/get_equipment_bookings`,
  create_EquipmentBooking: `${hostname}/create_equipment_booking`,
  update_EquipmentBooking: `${hostname}/update_equipment_booking`,
  cancel_EquipmentBooking: `${hostname}/cancel_equipment_booking`,
  update_EquipmentBooking_Status: `${hostname}/update_equipment_status`,
});
const AUTH = (baseUrl: string) => ({
  /** POST — exchange authorization code for tokens (backend adds client_secret) */
  tokenProxy: `${baseUrl}/auth/verify`,
  /** POST — refresh an expired access token using a refresh_token */
  refreshProxy: `${baseUrl}/auth/refresh`,
  /** GET  — retrieve the authenticated user's profile */
  fetch_UserInfo: `${baseUrl}/auth/get_user_info`,
});
const LLM = (hostname: string) => ({
  chatbot: `${hostname}/chatbot`,
});
const authConfig = AUTH(endpoint);
const apiConfig = {
  ...API(`${endpoint}/api`),
  fetch_UserInfo: authConfig.fetch_UserInfo,
};
export const LLMConfig = {
  ...LLM(endpoint),
};
export default apiConfig;

// ============================================================================
// Auth Provider Types
// ============================================================================

export type AuthProvider = "cognito" | "idprism" | "federate";

export interface ProviderConfig {
  /** Provider identifier */
  provider: AuthProvider;
  /** Display name shown on the login page */
  name: string;
  /** Description shown below the button */
  description: string;
  /** OAuth domain (e.g., https://xxx.auth.us-west-2.amazoncognito.com) */
  domain: string;
  /** OAuth Client ID */
  clientId: string;
  /** OAuth scopes (space-separated) */
  scopes: string;
  /** OAuth redirect URI after login */
  redirectUri: string;
  /** Whether this provider is fully configured and usable */
  enabled: boolean;
  /** Path for the authorization endpoint. Defaults to "/oauth2/authorize" (Cognito). */
  authorizePath?: string;
  /**
   * If set, the FE will POST to this backend URL for token exchange instead of
   * calling the provider's token endpoint directly. This is used when the provider
   * requires a client_secret that cannot be exposed in the browser.
   */
  tokenProxyUrl?: string;
  /**
   * If set, the FE will POST to this backend URL to refresh an expired access
   * token using the stored refresh_token. Falls back to `tokenProxyUrl` when
   * not specified.
   */
  refreshProxyUrl?: string;
  /**
   * If set, appended as `&auth_provider=<value>` to the Federate authorize URL
   * to skip discovery and route directly to a specific authentication provider.
   * Uses Federate's REQUEST_PARAMETER discovery strategy.
   * The allowed providers must be configured and allow-listed on the Federate profile.
   * E.g., "MIDWAY" for internal users, "IDPRISM_RADARPLUSSSO_BETA_NA" for CM engineers.
   */
  idpHint?: string;
  /**
   * RP-initiated logout (end-session) endpoint for providers that expose one — currently
   * only IdPrism. When set, buildProviderLogoutUrl redirects the browser here on logout so
   * the upstream IdP session is terminated and the user must re-enter credentials on the
   * next login (instead of being silently re-authenticated).
   *
   * NOT used by Cognito — its logout URL is derived from `domain` + `/logout`.
   * NOT set for Federate/Midway — Federate has no end-session endpoint, and we intentionally
   * leave the global Midway SSO session alone for internal users.
   *
   * IMPORTANT: the IdPrism value is tenant- AND stage-specific, and the app origin
   * (redirectUri without `/callback`) must be registered as an allowed post-logout redirect
   * URI on the IdPrism/Federate enrollment profile, or the redirect returns 403. Until the
   * RadarPlus IdPrism tenant URL is confirmed and allow-listed, leave this undefined so
   * logout stays local-only (no bogus redirect).
   */
  logoutUrl?: string;
}

// ============================================================================
// Per-Stage Cognito Configuration
// ============================================================================

const cognitoConfigByStage: Record<TStage, { domain: string; clientId: string }> = {
  Test: {
    domain: "https://us-west-2xx1z6whsl.auth.us-west-2.amazoncognito.com",
    clientId: "35jiiafuv9uopbjik9mkmboefj",
  },
  Alpha: {
    domain: "https://us-west-2xx1z6whsl.auth.us-west-2.amazoncognito.com",
    clientId: "35jiiafuv9uopbjik9mkmboefj",
  },
  Beta: {
    domain: "https://us-west-2zyr0qhk25.auth.us-west-2.amazoncognito.com",
    clientId: "1j51f3329j754aer98lc5i21ge",
  },
  Prod: {
    domain: "https://us-west-2cpg6vaz3h.auth.us-west-2.amazoncognito.com",
    clientId: "2cq5nbklhib9j5mb59brj14e0n",
  },
};

// ============================================================================
// Per-Stage Federate Configuration
// ============================================================================

const federateConfigByStage: Record<TStage, { domain: string; clientId: string }> = {
  Test: {
    domain: "https://idp-integ.federate.amazon.com",
    clientId: "dt-radarplus-beta-dev",
  },
  Alpha: {
    domain: "https://idp-integ.federate.amazon.com",
    clientId: "dt-radarplus-beta-dev",
  },
  Beta: {
    domain: "https://idp-integ.federate.amazon.com",
    clientId: "dt-radarplus-alpha-dev",
  },
  Prod: {
    domain: "https://idp.federate.amazon.com",
    clientId: "dt-radarplus-prod",
  },
};

// ============================================================================
// Per-Stage IdPrism Logout (end-session) Configuration
// ============================================================================
//
// IdPrism (used by the CM-engineer login, brokered through Federate) exposes an
// RP-initiated logout endpoint. Redirecting there on logout terminates the IdPrism
// session so CM engineers must re-enter credentials next time, instead of being
// silently re-authenticated.
//
// TODO(auth): fill in the RadarPlus IdPrism tenant end-session URL per stage, and
// register the app origin (StageOriginMap[stage]) as an allowed post-logout redirect
// URI on the IdPrism/Federate enrollment profile. Until then these stay `undefined`,
// which makes CM-engineer logout local-only (safe — no 403 redirect). Other apps use
// URLs of the shape `https://<region>.<stage-prefix>idprism-auth.amazon.dev/...`; the
// exact tenant path must be confirmed, not guessed.
const idprismLogoutUrlByStage: Record<TStage, string | undefined> = {
  Test: undefined,
  Alpha: undefined,
  Beta: undefined,
  Prod: undefined,
};

// ============================================================================
// Auth Redirect URI (stage-aware)
// ============================================================================

function getRedirectUri(): string {
  return `${StageOriginMap[currentStage]}/callback`;
}

// ============================================================================
// Auth Provider Configurations
// ============================================================================

const cognitoStageConfig = cognitoConfigByStage[currentStage];
const federateStageConfig = federateConfigByStage[currentStage];

export const AUTH_PROVIDERS: ProviderConfig[] = [
  {
    provider: "federate",
    name: "Internal User Login",
    description: "For internal users — sign in with Amazon Federate (Midway)",
    domain: federateStageConfig.domain,
    clientId: federateStageConfig.clientId,
    scopes: "openid",
    redirectUri: getRedirectUri(),
    enabled: true,
    authorizePath: "/api/oauth2/v1/authorize",
    tokenProxyUrl: authConfig.tokenProxy,
    refreshProxyUrl: authConfig.refreshProxy,
    idpHint: "midway",
  },
  {
    provider: "idprism",
    name: "CM Engineer Login",
    description: "For CM engineers — sign in with IdPrism",
    // IdPrism goes through Federate with an idpHint to skip discovery
    // and route directly to the IdPrism login page.
    domain: federateStageConfig.domain,
    // clientId is stage-driven (federateConfigByStage). `dt-radarplus-beta-dev`
    // uses the new NextGen profile configuration, whereas `dt-radarplus-alpha-dev`
    // is still on the legacy configuration.
    clientId: federateStageConfig.clientId,
    scopes: "openid",
    redirectUri: getRedirectUri(),
    enabled: true,
    authorizePath: "/api/oauth2/v1/authorize",
    tokenProxyUrl: authConfig.tokenProxy,
    refreshProxyUrl: authConfig.refreshProxy,
    idpHint: "idprism",
    // Undefined until the RadarPlus IdPrism tenant end-session URL is confirmed and
    // the post-logout redirect is allow-listed (see idprismLogoutUrlByStage above).
    logoutUrl: idprismLogoutUrlByStage[currentStage],
  },
  {
    provider: "cognito",
    name: "Operator Login",
    description: "For operators — sign in with your Cognito account",
    domain: cognitoStageConfig.domain,
    clientId: cognitoStageConfig.clientId,
    scopes: "openid email phone",
    redirectUri: getRedirectUri(),
    enabled: true,
  },
];

/**
 * Get provider config by provider name.
 */
export function getProviderConfig(provider: AuthProvider): ProviderConfig | undefined {
  return AUTH_PROVIDERS.find(p => p.provider === provider);
}
